import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type LiveStatus = "connecting" | "connected" | "disconnected";

export function useRealtimeDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [liveStatus, setLiveStatus] = useState<LiveStatus>("connecting");

  useEffect(() => {
    if (!user?.id) {
      setLiveStatus("disconnected");
      return;
    }

    setLiveStatus("connecting");

    const channel = supabase
      .channel(`dashboard:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "trades",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Invalidate all dashboard-related queries to trigger refetch
          queryClient.invalidateQueries({ queryKey: ["dashboard-trades", user.id] });
          queryClient.invalidateQueries({ queryKey: ["dashboard-violations", user.id] });
          queryClient.invalidateQueries({ queryKey: ["dashboard-raw-trades", user.id] });
          queryClient.invalidateQueries({ queryKey: ["active-prop-challenge", user.id] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "violations",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["dashboard-violations", user.id] });
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") setLiveStatus("connected");
        else if (status === "CLOSED" || status === "CHANNEL_ERROR") setLiveStatus("disconnected");
        else setLiveStatus("connecting");
      });

    return () => {
      supabase.removeChannel(channel);
      setLiveStatus("disconnected");
    };
  }, [user?.id, queryClient]);

  return { liveStatus };
}
